/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetRewardsMeRow, GetRewardsQuestsRow, GetRewardsLadderRow, GetRewardsStreakRow, GetRewardsMilestonesRow, GetRewardsLeaderboardRow, GetRewardsProfileRow, GetRewardsReferralsRow, GetRewardsContentRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String } from '../../helpers/index.js';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';


// ─── useGetRewardsMe ────────────────────────────────────────────────────────────

export interface useGetRewardsMeArgs { readonly fields?: readonly ('points' | 'rank' | 'level' | 'streak' | 'xConnected' | 'xHandle' | 'xName' | 'avatar' | 'telegramLinked' | 'account' | 'qualityScore' | 'frozen' | 'participantExists' | 'joinedAt')[]; }


async function _useGetRewardsMeQueryFn(
  args: useGetRewardsMeArgs,
  ctx: QueryFunctionContext,
): Promise<GetRewardsMeRow | null> {
  const url = '/v1/rewards/me';
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetRewardsMe',
    routeTemplate: '/v1/rewards/me',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetRewardsMeRow;
}


const _useGetRewardsMeFactory = Object.assign(
  (args: useGetRewardsMeArgs) => ({
    queryKey: ['rewards', 'detail', args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetRewardsMeQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetRewardsMe',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['rewards', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetRewardsMeArgs, GetRewardsMeRow>;


export const useGetRewardsMeSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/rewards/me',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetRewardsMeFactory },
  parseInput: (raw: unknown): useGetRewardsMeArgs => raw as useGetRewardsMeArgs,
  parseResponse: (raw: unknown): GetRewardsMeRow => raw as GetRewardsMeRow,
  specName: 'useGetRewardsMe' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetRewardsMeRow>;


// ─── useGetRewardsQuests ────────────────────────────────────────────────────────────

export interface useGetRewardsQuestsArgs { readonly viewportGated?: boolean; readonly fields?: readonly ('id' | 'title' | 'description' | 'points' | 'verifyMethod' | 'ctaMode' | 'actionUrl' | 'isNew' | 'status' | 'assurance')[]; }


async function _useGetRewardsQuestsQueryFn(
  args: useGetRewardsQuestsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetRewardsQuestsRow>> {
  const url = '/v1/rewards/quests';
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetRewardsQuests',
    routeTemplate: '/v1/rewards/quests',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetRewardsQuestsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetRewardsQuestsFactory = Object.assign(
  (args: useGetRewardsQuestsArgs) => ({
    queryKey: ['rewards', 'list', ((args.fields) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetRewardsQuestsQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetRewardsQuests',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['rewards', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetRewardsQuestsArgs, PagedData<GetRewardsQuestsRow>>;


export const useGetRewardsQuestsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/rewards/quests',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetRewardsQuestsFactory },
  parseInput: (raw: unknown): useGetRewardsQuestsArgs => raw as useGetRewardsQuestsArgs,
  parseResponse: (raw: unknown): PagedData<GetRewardsQuestsRow> => raw as PagedData<GetRewardsQuestsRow>,
  specName: 'useGetRewardsQuests' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetRewardsQuestsRow>>;
export const useGetRewardsQuestsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetRewardsQuestsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetRewardsQuestsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetRewardsQuestsPerEntitySubjects(_args: useGetRewardsQuestsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetRewardsQuestsRowSubjectTemplates(_args: useGetRewardsQuestsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetRewardsQuestsApplyEvent(
  prev: PagedData<GetRewardsQuestsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetRewardsQuestsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetRewardsQuestsRealtime = {
  domain: 'rewards' as const,
  domainPins: [] as readonly string[],
  rowKey: 'id' as const,
  perEntitySubjects: (args: useGetRewardsQuestsArgs) => _useGetRewardsQuestsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetRewardsQuestsArgs) => _useGetRewardsQuestsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetRewardsQuestsArgs) =>
    (prev: PagedData<GetRewardsQuestsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetRewardsQuestsRow> | undefined =>
      _useGetRewardsQuestsApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetRewardsQuestsRow>, unknown>;


// ─── useGetRewardsLadder ────────────────────────────────────────────────────────────

export interface useGetRewardsLadderArgs { readonly viewportGated?: boolean; readonly fields?: readonly ('id' | 'order' | 'key' | 'name' | 'minPoints')[]; }


async function _useGetRewardsLadderQueryFn(
  args: useGetRewardsLadderArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetRewardsLadderRow>> {
  const url = '/v1/rewards/ladder';
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetRewardsLadder',
    routeTemplate: '/v1/rewards/ladder',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetRewardsLadderRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetRewardsLadderFactory = Object.assign(
  (args: useGetRewardsLadderArgs) => ({
    queryKey: ['rewards', 'list', ((args.fields) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetRewardsLadderQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetRewardsLadder',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['rewards', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetRewardsLadderArgs, PagedData<GetRewardsLadderRow>>;


export const useGetRewardsLadderSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/rewards/ladder',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetRewardsLadderFactory },
  parseInput: (raw: unknown): useGetRewardsLadderArgs => raw as useGetRewardsLadderArgs,
  parseResponse: (raw: unknown): PagedData<GetRewardsLadderRow> => raw as PagedData<GetRewardsLadderRow>,
  specName: 'useGetRewardsLadder' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetRewardsLadderRow>>;
export const useGetRewardsLadderPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetRewardsLadderArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetRewardsLadderQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetRewardsLadderPerEntitySubjects(_args: useGetRewardsLadderArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetRewardsLadderRowSubjectTemplates(_args: useGetRewardsLadderArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetRewardsLadderApplyEvent(
  prev: PagedData<GetRewardsLadderRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetRewardsLadderRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetRewardsLadderRealtime = {
  domain: 'rewards' as const,
  domainPins: [] as readonly string[],
  rowKey: 'id' as const,
  perEntitySubjects: (args: useGetRewardsLadderArgs) => _useGetRewardsLadderPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetRewardsLadderArgs) => _useGetRewardsLadderRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetRewardsLadderArgs) =>
    (prev: PagedData<GetRewardsLadderRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetRewardsLadderRow> | undefined =>
      _useGetRewardsLadderApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetRewardsLadderRow>, unknown>;


// ─── useGetRewardsStreak ────────────────────────────────────────────────────────────

export interface useGetRewardsStreakArgs { readonly fields?: readonly ('current' | 'longest' | 'level' | 'today' | 'timezone' | 'dayBoundaryLabel' | 'tradedToday' | 'claimedToday' | 'dailyPoints' | 'week')[]; }


async function _useGetRewardsStreakQueryFn(
  args: useGetRewardsStreakArgs,
  ctx: QueryFunctionContext,
): Promise<GetRewardsStreakRow | null> {
  const url = '/v1/rewards/streak';
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetRewardsStreak',
    routeTemplate: '/v1/rewards/streak',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetRewardsStreakRow;
}


const _useGetRewardsStreakFactory = Object.assign(
  (args: useGetRewardsStreakArgs) => ({
    queryKey: ['rewards', 'detail', args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetRewardsStreakQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetRewardsStreak',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['rewards', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetRewardsStreakArgs, GetRewardsStreakRow>;


export const useGetRewardsStreakSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/rewards/streak',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetRewardsStreakFactory },
  parseInput: (raw: unknown): useGetRewardsStreakArgs => raw as useGetRewardsStreakArgs,
  parseResponse: (raw: unknown): GetRewardsStreakRow => raw as GetRewardsStreakRow,
  specName: 'useGetRewardsStreak' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetRewardsStreakRow>;


// ─── useGetRewardsMilestones ────────────────────────────────────────────────────────────

export interface useGetRewardsMilestonesArgs { readonly viewportGated?: boolean; readonly fields?: readonly ('id' | 'title' | 'description' | 'track' | 'targetCount' | 'rewardPoints' | 'progress' | 'reached' | 'claimed' | 'claimedAt')[]; }


async function _useGetRewardsMilestonesQueryFn(
  args: useGetRewardsMilestonesArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetRewardsMilestonesRow>> {
  const url = '/v1/rewards/milestones';
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetRewardsMilestones',
    routeTemplate: '/v1/rewards/milestones',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetRewardsMilestonesRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetRewardsMilestonesFactory = Object.assign(
  (args: useGetRewardsMilestonesArgs) => ({
    queryKey: ['rewards', 'list', ((args.fields) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetRewardsMilestonesQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetRewardsMilestones',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['rewards', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetRewardsMilestonesArgs, PagedData<GetRewardsMilestonesRow>>;


export const useGetRewardsMilestonesSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/rewards/milestones',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetRewardsMilestonesFactory },
  parseInput: (raw: unknown): useGetRewardsMilestonesArgs => raw as useGetRewardsMilestonesArgs,
  parseResponse: (raw: unknown): PagedData<GetRewardsMilestonesRow> => raw as PagedData<GetRewardsMilestonesRow>,
  specName: 'useGetRewardsMilestones' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetRewardsMilestonesRow>>;
export const useGetRewardsMilestonesPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetRewardsMilestonesArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetRewardsMilestonesQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetRewardsMilestonesPerEntitySubjects(_args: useGetRewardsMilestonesArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetRewardsMilestonesRowSubjectTemplates(_args: useGetRewardsMilestonesArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetRewardsMilestonesApplyEvent(
  prev: PagedData<GetRewardsMilestonesRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetRewardsMilestonesRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetRewardsMilestonesRealtime = {
  domain: 'rewards' as const,
  domainPins: [] as readonly string[],
  rowKey: 'id' as const,
  perEntitySubjects: (args: useGetRewardsMilestonesArgs) => _useGetRewardsMilestonesPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetRewardsMilestonesArgs) => _useGetRewardsMilestonesRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetRewardsMilestonesArgs) =>
    (prev: PagedData<GetRewardsMilestonesRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetRewardsMilestonesRow> | undefined =>
      _useGetRewardsMilestonesApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetRewardsMilestonesRow>, unknown>;


// ─── useGetRewardsLeaderboard ────────────────────────────────────────────────────────────

export interface useGetRewardsLeaderboardArgs { readonly fields?: readonly ('rows' | 'me')[]; }


async function _useGetRewardsLeaderboardQueryFn(
  args: useGetRewardsLeaderboardArgs,
  ctx: QueryFunctionContext,
): Promise<GetRewardsLeaderboardRow | null> {
  const url = '/v1/rewards/leaderboard';
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetRewardsLeaderboard',
    routeTemplate: '/v1/rewards/leaderboard',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetRewardsLeaderboardRow;
}


const _useGetRewardsLeaderboardFactory = Object.assign(
  (args: useGetRewardsLeaderboardArgs) => ({
    queryKey: ['rewards', 'detail', args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetRewardsLeaderboardQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetRewardsLeaderboard',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['rewards', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetRewardsLeaderboardArgs, GetRewardsLeaderboardRow>;


export const useGetRewardsLeaderboardSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/rewards/leaderboard',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetRewardsLeaderboardFactory },
  parseInput: (raw: unknown): useGetRewardsLeaderboardArgs => raw as useGetRewardsLeaderboardArgs,
  parseResponse: (raw: unknown): GetRewardsLeaderboardRow => raw as GetRewardsLeaderboardRow,
  specName: 'useGetRewardsLeaderboard' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetRewardsLeaderboardRow>;


// ─── useGetRewardsProfile ────────────────────────────────────────────────────────────

export interface useGetRewardsProfileArgs { readonly handle: string; readonly fields?: readonly ('handle' | 'avatar' | 'points' | 'rank' | 'level' | 'streak' | 'badges' | 'referralCount')[]; }


async function _useGetRewardsProfileQueryFn(
  args: useGetRewardsProfileArgs,
  ctx: QueryFunctionContext,
): Promise<GetRewardsProfileRow | null> {
  if (args.handle === undefined || args.handle === null || args.handle === '') return null;
  const url = `/v1/rewards/profile/${args.handle}`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetRewardsProfile',
    routeTemplate: '/v1/rewards/profile/{handle}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetRewardsProfileRow;
}


const _useGetRewardsProfileFactory = Object.assign(
  (args: useGetRewardsProfileArgs) => ({
    queryKey: ['rewards', 'detail', args.handle] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetRewardsProfileQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetRewardsProfile',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['rewards', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetRewardsProfileArgs, GetRewardsProfileRow>;


export const useGetRewardsProfileSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/rewards/profile/{handle}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetRewardsProfileFactory },
  parseInput: (raw: unknown): useGetRewardsProfileArgs => raw as useGetRewardsProfileArgs,
  parseResponse: (raw: unknown): GetRewardsProfileRow => raw as GetRewardsProfileRow,
  specName: 'useGetRewardsProfile' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetRewardsProfileRow>;


// ─── useGetRewardsReferrals ────────────────────────────────────────────────────────────

export interface useGetRewardsReferralsArgs { readonly fields?: readonly ('link' | 'code' | 'referred' | 'l1Total' | 'l2Total' | 'overrideTodayRemaining')[]; }


async function _useGetRewardsReferralsQueryFn(
  args: useGetRewardsReferralsArgs,
  ctx: QueryFunctionContext,
): Promise<GetRewardsReferralsRow | null> {
  const url = '/v1/rewards/referrals';
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetRewardsReferrals',
    routeTemplate: '/v1/rewards/referrals',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetRewardsReferralsRow;
}


const _useGetRewardsReferralsFactory = Object.assign(
  (args: useGetRewardsReferralsArgs) => ({
    queryKey: ['rewards', 'detail', args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetRewardsReferralsQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetRewardsReferrals',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['rewards', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetRewardsReferralsArgs, GetRewardsReferralsRow>;


export const useGetRewardsReferralsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/rewards/referrals',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetRewardsReferralsFactory },
  parseInput: (raw: unknown): useGetRewardsReferralsArgs => raw as useGetRewardsReferralsArgs,
  parseResponse: (raw: unknown): GetRewardsReferralsRow => raw as GetRewardsReferralsRow,
  specName: 'useGetRewardsReferrals' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetRewardsReferralsRow>;


// ─── useGetRewardsContent ────────────────────────────────────────────────────────────

export interface useGetRewardsContentArgs { readonly fields?: readonly ('submissions' | 'capsRemaining')[]; }


async function _useGetRewardsContentQueryFn(
  args: useGetRewardsContentArgs,
  ctx: QueryFunctionContext,
): Promise<GetRewardsContentRow | null> {
  const url = '/v1/rewards/content';
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetRewardsContent',
    routeTemplate: '/v1/rewards/content',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetRewardsContentRow;
}


const _useGetRewardsContentFactory = Object.assign(
  (args: useGetRewardsContentArgs) => ({
    queryKey: ['rewards', 'detail', args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetRewardsContentQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetRewardsContent',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['rewards', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetRewardsContentArgs, GetRewardsContentRow>;


export const useGetRewardsContentSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/rewards/content',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetRewardsContentFactory },
  parseInput: (raw: unknown): useGetRewardsContentArgs => raw as useGetRewardsContentArgs,
  parseResponse: (raw: unknown): GetRewardsContentRow => raw as GetRewardsContentRow,
  specName: 'useGetRewardsContent' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetRewardsContentRow>;
